// src/antdviews/components/EdfTaskContent.tsx

// EDF 任务列表内容组件

import React from 'react'
import {
    Layout,
    Table,
    Tag,
    Button,
    Tooltip,
    Pagination,
    Space,
    Card,
    Modal,
    Row,
    Col,
    Select,
    Typography,
    message,
    Spin,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
    FileTextOutlined,
    ReloadOutlined,
} from '@ant-design/icons'
import axios from 'axios'
import { Global } from '../../services/Global'
import { DEFAULT_RPP } from '../../services/Macro'
import { mathUtil } from '../../utils/MathUtil'
import { browserUtil } from '../../utils/BrowserUtil'
import { encrypUtil } from '../../utils/EncrypUtil'
import { DrpApi } from '../../services/Api'
// import { DrpApi } from '../../services/DrpApi'
// import { ROLE } from '../../services/Role'

const { Content } = Layout
const { Title, Text } = Typography

/** 服务器返回的原始步骤结构 */
export interface EdfTaskStepRaw {
    id: string
    taskId: string
    stepName: string
    stepOrder: number
    status: string
    stepMessage?: string
    startedAt?: string | null
    finishedAt?: string | null
    durationMs?: string | number | null
    createdAt?: string | null
    updatedAt?: string | null
}

/** 前端展示用的步骤结构 */
export interface EdfTaskStep {
    /** 步骤名，如 CREATE_TASK / GENERATE_EDF ... */
    step: string
    /** 步骤展示文本 */
    stepText?: string
    /** 步骤顺序 */
    stepOrder?: number
    /** 步骤状态：SUCCESS / SKIPPED / PENDING / RUNNING / FAILED */
    state?: string
    /** 步骤状态展示文本 */
    stateText?: string
    /** 步骤状态颜色：success / processing / default / error */
    stateColor?: string
    /** 步骤消息 */
    message?: string
    /** 步骤开始时间（ISO 字符串或时间戳） */
    startedAt?: string | number | null
    /** 步骤开始时间格式化文本 */
    startedAtText?: string
    /** 步骤结束时间（ISO 字符串或时间戳） */
    finishedAt?: string | number | null
    /** 步骤结束时间格式化文本 */
    finishedAtText?: string
    /** 耗时展示文本 */
    durationText?: string
}

export interface EdfTaskItem {
    id: string
    taskId: string
    recordUrl: string
    remark?: string
    edfSegmentCount: number
    edfSegmentMinutes: number
    status: string
    currentStep?: string
    stepMessage?: string
    reportReady: boolean | number
    documentId: string | null
    createdAt: string
    updatedAt: string
    finishedAt: string | null

    // 前端加工字段
    statusText?: string
    statusColor?: string
    canViewReport?: boolean
    segmentInfo?: string
    createdAtText?: string
    updatedAtText?: string
    finishedAtText?: string
    recordUrlShort?: string

    // 步骤列表（详情弹窗用）
    steps?: EdfTaskStep[]
}

export interface EdfTaskData {
    list: EdfTaskItem[]
    totalPages: number
    pages?: number[]
    totalRecords?: number
    page?: number
    size?: number
}

interface Props {
    tasks: EdfTaskData
    trans: (key: string) => string
    changeTaskList: (size: number, page: number) => void
}

interface State {
    taskSize: number
    taskPage: number
    detailVisible: boolean
    detailTask: EdfTaskItem | null
    detailLoading: boolean
}

export default class EdfTaskContent extends React.Component<Props, State> {
    state: State = {
        taskSize: 10,
        taskPage: 1,
        detailVisible: false,
        detailTask: null,
        detailLoading: false,
    }

    componentWillMount() {
        Global.isMobile = browserUtil.checkMobile()
    }

    /** 多语言包装，找不到 key 时回退到默认文本 */
    t = (key: string, fallback?: string): string => {
        const v = this.props.trans ? this.props.trans(key) : ''
        if (!v || v === key) return fallback ?? key
        return v
    }

    onLppChange = (value: number) => {
        const oldSize = this.state.taskSize
        this.setState({ taskSize: value }, () => {
            this.setState(
                {
                    taskPage: mathUtil.getNewPage(
                        oldSize,
                        this.state.taskPage,
                        this.state.taskSize
                    ),
                },
                () => this.updateTaskList()
            )
        })
    }

    updateTaskList = () => {
        this.props.changeTaskList(this.state.taskSize, this.state.taskPage)
    }

    onTaskPageChange = (page: number) => {
        this.setState({ taskPage: page }, () => this.updateTaskList())
    }

    refresh = () => {
        this.updateTaskList()
    }

    onViewReport = (task: EdfTaskItem) => {
        if (!task.documentId) return
        // TODO: 替换成实际的报告路由/下载接口
        window.open(`/report/${task.documentId}`, '_blank')
    }

    /** 打开详情弹窗，弹窗内展示按时间顺序的步骤表格 */
    showTaskDetail = async (taskId: string) => {
        this.setState({ detailVisible: true, detailTask: null, detailLoading: true })
        const task = await this.getTaskDetail(taskId)
        this.setState({ detailLoading: false, detailTask: task })
    }

    /** 请求任务详情 */
    getTaskDetail = async (taskId: string): Promise<EdfTaskItem | null> => {
        const DEBUG = true
        const TAG = `EdfTaskContent.getTaskDetail() - `
        const uuid = 'system'
        const token = localStorage.getItem('token') || null
        const lang = localStorage.getItem('lang') || 'zh_CN'

        const requestData = {
            token,
            uuid,
            lang: lang.split('_')[0],
            taskId,
        }

        try {
            DEBUG && console.log(TAG, `requestData:\n`, requestData)

            const params = await encrypUtil.encodeRequestRsa(requestData)
            DEBUG && console.log(TAG, `params:\n`, params)

            const response = await axios.post(DrpApi.GET_TASK_DETAIL, params)
            DEBUG && console.log(TAG, `获取EDF任务详情:\n`, response.data.data)

            const data = response.data.data as {
                task: EdfTaskItem
                steps: EdfTaskStepRaw[]
            }
            const { task, steps } = data || {}

            if (!task) {
                message.error(this.t('edf_task_detail_not_exist', '任务不存在'))
                return null
            }

            // 把原始 task + steps 转成 EdfTaskItem
            const detailTask: EdfTaskItem = {
                ...task,
                statusText: task.statusText ?? this.getStatusText(task.status),
                statusColor: task.statusColor ?? this.getStatusColor(task.status),
                segmentInfo:
                    task.segmentInfo ??
                    (task.edfSegmentCount != null
                        ? this.t('edf_task_segment_info', '{count} 段 / {minutes} 分钟')
                            .replace('{count}', String(task.edfSegmentCount))
                            .replace('{minutes}', String(task.edfSegmentMinutes ?? 0))
                        : '-'),
                createdAtText: task.createdAtText ?? this.formatTime(task.createdAt),
                updatedAtText: task.updatedAtText ?? this.formatTime(task.updatedAt),
                finishedAtText: task.finishedAtText ?? this.formatTime(task.finishedAt),
                recordUrlShort: task.recordUrlShort ?? this.shortenUrl(task.recordUrl),
                // 注意：服务器返回的是 0/1，需要转成布尔
                reportReady: !!task.reportReady,
                canViewReport:
                    task.canViewReport ?? (!!task.reportReady && !!task.documentId),
                steps: (steps || []).map((s) => this.mapStep(s)),
            }

            return detailTask
        } catch (error) {
            console.error(TAG, error)
            message.error(this.t('edf_task_detail_fetch_fail', '获取任务详情失败'))
            return null
        }
    }

    /** 把服务器返回的步骤映射为前端展示结构 */
    mapStep = (s: EdfTaskStepRaw): EdfTaskStep => {
        return {
            step: s.stepName,
            stepText: this.getStepNameText(s.stepName),
            stepOrder: s.stepOrder,
            state: s.status,
            stateText: this.getStepStateText(s.status),
            stateColor: this.getStepStateColor(s.status),
            message: s.stepMessage,
            startedAt: s.startedAt ?? null,
            startedAtText: this.formatTime(s.startedAt),
            finishedAt: s.finishedAt ?? null,
            finishedAtText: this.formatTime(s.finishedAt),
            durationText: this.formatDuration(s.durationMs),
        }
    }

    /** 任务状态 -> 颜色 */
    getStatusColor = (status: string): string => {
        switch (status) {
            case 'DONE':
            case 'SUCCESS':
            case 'FINISHED':
                return 'success'
            case 'DOING':
            case 'RUNNING':
            case 'PENDING':
                return 'processing'
            case 'FAIL':
            case 'FAILED':
            case 'ERROR':
                return 'error'
            default:
                return 'default'
        }
    }

    /** 步骤状态 -> 颜色 */
    getStepStateColor = (state?: string): string => {
        switch (state) {
            case 'SUCCESS':
            case 'DONE':
                return 'success'
            case 'RUNNING':
            case 'DOING':
                return 'processing'
            case 'FAIL':
            case 'FAILED':
            case 'ERROR':
                return 'error'
            case 'SKIPPED':
                return 'warning'
            case 'PENDING':
            case 'WAIT':
            default:
                return 'default'
        }
    }

    /** 步骤状态 -> 展示文本 */
    getStepStateText = (state?: string): string => {
        switch (state) {
            case 'SUCCESS':
            case 'DONE':
                return this.t('edf_task_step_state_success', '成功')
            case 'RUNNING':
            case 'DOING':
                return this.t('edf_task_step_state_running', '进行中')
            case 'FAIL':
            case 'FAILED':
            case 'ERROR':
                return this.t('edf_task_step_state_fail', '失败')
            case 'SKIPPED':
                return this.t('edf_task_step_state_skipped', '已跳过')
            case 'PENDING':
            case 'WAIT':
                return this.t('edf_task_step_state_pending', '等待中')
            default:
                return state || '-'
        }
    }

    /** 步骤名 -> 展示文本 */
    getStepNameText = (stepName?: string): string => {
        switch (stepName) {
            case 'CREATE_TASK':
                return this.t('edf_task_step_name_create_task', '创建任务')
            case 'GENERATE_EDF':
                return this.t('edf_task_step_name_generate_edf', '生成 EDF')
            case 'UPLOAD_EDF':
                return this.t('edf_task_step_name_upload_edf', '上传 EDF')
            case 'DRP_ANALYSIS':
                return this.t('edf_task_step_name_drp_analysis', 'DRP 分析')
            case 'REPORT_GENERATE':
                return this.t('edf_task_step_name_report_generate', '生成报告')
            default:
                return stepName || '-'
        }
    }

    /** 任务状态 -> 展示文本 */
    getStatusText = (status: string): string => {
        switch (status) {
            case 'CREATED':
                return this.t('edf_task_status_created', '已创建')
            case 'RUNNING':
                return this.t('edf_task_status_running', '进行中')
            case 'DONE':
            case 'FINISHED':
                return this.t('edf_task_status_finished', '已完成')
            case 'FAILED':
                return this.t('edf_task_status_failed', '失败')
            default:
                return status
        }
    }

    formatTime = (t?: string | number | null): string => {
        if (!t) return '-'
        const d = new Date(t)
        if (isNaN(d.getTime())) return String(t)
        const pad = (n: number) => String(n).padStart(2, '0')
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    }

    /** 毫秒 -> 可读耗时，如 1m 26s */
    formatDuration = (ms?: string | number | null): string => {
        if (ms == null || ms === '') return '-'
        const n = typeof ms === 'string' ? Number(ms) : ms
        if (isNaN(n) || n < 0) return '-'
        if (n < 1000) return `${n}ms`
        const totalSec = Math.floor(n / 1000)
        const h = Math.floor(totalSec / 3600)
        const m = Math.floor((totalSec % 3600) / 60)
        const sec = totalSec % 60
        const parts: string[] = []
        if (h > 0) parts.push(`${h}h`)
        if (m > 0) parts.push(`${m}m`)
        if (sec > 0 || parts.length === 0) parts.push(`${sec}s`)
        return parts.join(' ')
    }

    shortenUrl = (url?: string): string => {
        if (!url) return '-'
        // recordUrl 是哈希串，不是标准 URL，直接截断
        return url.length > 16 ? url.slice(0, 16) + '…' : url
    }

    closeTaskDetail = () => {
        this.setState({ detailVisible: false, detailTask: null, detailLoading: false })
    }

    /** PC 端主表格列 */
    getPcColumns = (): ColumnsType<EdfTaskItem> => {
        return [
            {
                title: this.t('edf_task_col_task_id', '任务ID'),
                dataIndex: 'taskId',
                key: 'taskId',
                width: 170,
                ellipsis: true,
                render: (v: string) => (
                    <Tooltip title={v}>
                        <span style={{ fontFamily: 'monospace' }}>{v}</span>
                    </Tooltip>
                ),
            },
            {
                title: this.t('edf_task_col_record_url', '记录URL'),
                dataIndex: 'recordUrl',
                key: 'recordUrl',
                width: 140,
                ellipsis: true,
                render: (_: any, row) => (
                    <Tooltip title={row.recordUrl}>
                        <span style={{ fontFamily: 'monospace' }}>{row.recordUrlShort}</span>
                    </Tooltip>
                ),
            },
            {
                title: this.t('edf_task_col_status', '状态'),
                dataIndex: 'status',
                key: 'status',
                width: 100,
                render: (_: any, row) => (
                    <Tag color={row.statusColor}>{row.statusText}</Tag>
                ),
            },
            {
                title: this.t('edf_task_col_segment', '分段'),
                dataIndex: 'segmentInfo',
                key: 'segmentInfo',
                width: 130,
                render: (v: string) => v || '-',
            },
            {
                title: this.t('edf_task_col_report', '报告'),
                dataIndex: 'reportReady',
                key: 'reportReady',
                width: 90,
                render: (_: any, row) =>
                    row.reportReady ? (
                        <Tag color="cyan">{this.t('edf_task_report_ready', '已就绪')}</Tag>
                    ) : (
                        <Tag>{this.t('edf_task_report_not_ready', '未就绪')}</Tag>
                    ),
            },
            {
                title: this.t('edf_task_col_created_at', '创建时间'),
                dataIndex: 'createdAtText',
                key: 'createdAt',
                width: 160,
                render: (v: string) => <Text type="secondary">{v}</Text>,
            },
            {
                title: this.t('edf_task_col_finished_at', '完成时间'),
                dataIndex: 'finishedAtText',
                key: 'finishedAt',
                width: 160,
                render: (v: string) => <Text type="secondary">{v}</Text>,
            },
            {
                title: this.t('edf_task_col_action', '操作'),
                key: 'action',
                width: 140,
                render: (_: any, row) => (
                    <Space size="small">
                        <Button type="link" size="small" onClick={() => this.showTaskDetail(row.taskId)}>
                            {this.t('edf_task_action_detail', '详情')}
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            disabled={!row.canViewReport}
                            onClick={() => this.onViewReport(row)}
                        >
                            {this.t('edf_task_action_report', '报告')}
                        </Button>
                    </Space>
                ),
            },
        ]
    }

    /** 移动端主表格列 */
    getMobileColumns = (): ColumnsType<EdfTaskItem> => {
        return [
            {
                title: '',
                key: 'action',
                width: 40,
                render: (_: any, row) => (
                    <Button
                        type="link"
                        size="small"
                        icon={<span style={{ fontSize: 14 }}>▾</span>}
                        onClick={() => this.showTaskDetail(row.taskId)}
                    />
                ),
            },
            {
                title: this.t('edf_task_col_task_id', '任务ID'),
                dataIndex: 'taskId',
                key: 'taskId',
                ellipsis: true,
            },
            {
                title: this.t('edf_task_col_status', '状态'),
                dataIndex: 'status',
                key: 'status',
                width: 90,
                render: (_: any, row) => (
                    <Tag color={row.statusColor}>{row.statusText}</Tag>
                ),
            },
            {
                title: this.t('edf_task_col_created_at', '创建时间'),
                dataIndex: 'createdAtText',
                key: 'createdAt',
                width: 150,
            },
        ]
    }

    /** 详情弹窗内的步骤表格列 */
    getStepColumns = (): ColumnsType<EdfTaskStep> => {
        return [
            {
                title: '#',
                key: 'index',
                width: 50,
                render: (_: any, row: EdfTaskStep, index: number) =>
                    row.stepOrder ?? index + 1,
            },
            {
                title: this.t('edf_task_step_col_started_at', '开始时间'),
                dataIndex: 'startedAtText',
                key: 'startedAtText',
                width: 170,
                render: (v: string) => <Text type="secondary">{v || '-'}</Text>,
            },
            {
                title: this.t('edf_task_step_col_finished_at', '结束时间'),
                dataIndex: 'finishedAtText',
                key: 'finishedAtText',
                width: 170,
                render: (v: string) => <Text type="secondary">{v || '-'}</Text>,
            },
            {
                title: this.t('edf_task_step_col_duration', '耗时'),
                dataIndex: 'durationText',
                key: 'durationText',
                width: 90,
                render: (v: string) => <Text type="secondary">{v || '-'}</Text>,
            },
            {
                title: this.t('edf_task_step_col_step', '步骤'),
                dataIndex: 'stepText',
                key: 'stepText',
                width: 160,
                ellipsis: true,
                render: (v: string, row) => (
                    <Tooltip title={row.step || ''}>
                        <span>{v || row.step || '-'}</span>
                    </Tooltip>
                ),
            },
            {
                title: this.t('edf_task_step_col_status', '状态'),
                dataIndex: 'stateText',
                key: 'stateText',
                width: 90,
                render: (_: any, row) => (
                    <Tag color={row.stateColor}>
                        {row.stateText || row.state || '-'}
                    </Tag>
                ),
            },
            {
                title: this.t('edf_task_step_col_message', '消息'),
                dataIndex: 'message',
                key: 'message',
                ellipsis: true,
                render: (v: string) => v || '-',
            },
        ]
    }

    /** 详情弹窗 */
    renderDetailModal = () => {
        const { detailVisible, detailTask, detailLoading } = this.state

        const steps = detailTask?.steps || []

        return (
            <Modal
                title={
                    <Space>
                        <FileTextOutlined style={{ color: '#1890ff' }} />
                        <span>{this.t('edf_task_detail_title', '任务详情')}</span>
                        {detailTask && (
                            <Tag color="blue" style={{ fontFamily: 'monospace' }}>
                                {detailTask.taskId}
                            </Tag>
                        )}
                    </Space>
                }
                open={detailVisible}
                onCancel={this.closeTaskDetail}
                footer={[
                    <Button key="close" onClick={this.closeTaskDetail}>
                        {this.t('common_close', '关闭')}
                    </Button>,
                ]}
                width={1000}
                destroyOnClose
            >
                {detailLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px 0' }}>
                        <Spin tip={this.t('edf_task_detail_loading', '正在获取任务详情...')} />
                    </div>
                ) : detailTask ? (
                    <>
                        {/* 任务概要 */}
                        <div style={{ marginBottom: 12 }}>
                            <Space size="large" wrap>
                                <span>
                                    {this.t('edf_task_detail_status', '状态')}：<Tag color={detailTask.statusColor}>
                                        {detailTask.statusText}
                                    </Tag>
                                </span>
                                <span>
                                    {this.t('edf_task_col_segment', '分段')}：{detailTask.segmentInfo || '-'}
                                </span>
                                <span>
                                    {this.t('edf_task_col_report', '报告')}：
                                    {detailTask.reportReady ? (
                                        <Tag color="cyan">{this.t('edf_task_report_ready', '已就绪')}</Tag>
                                    ) : (
                                        <Tag>{this.t('edf_task_report_not_ready', '未就绪')}</Tag>
                                    )}
                                </span>
                                <span>
                                    {this.t('edf_task_detail_created', '创建')}：{detailTask.createdAtText || '-'}
                                </span>
                                <span>
                                    {this.t('edf_task_detail_finished', '完成')}：{detailTask.finishedAtText || '-'}
                                </span>
                            </Space>
                        </div>

                        {/* 备注 */}
                        {detailTask.remark && (
                            <div style={{ marginBottom: 12 }}>
                                <Text type="secondary">
                                    {this.t('edf_task_detail_remark', '备注')}：{detailTask.remark}
                                </Text>
                            </div>
                        )}

                        {/* 步骤表格（按 stepOrder 顺序） */}
                        <Table<EdfTaskStep>
                            rowKey={(record, index) => `${record.step}-${index}`}
                            columns={this.getStepColumns()}
                            dataSource={steps}
                            pagination={false}
                            size="small"
                            bordered
                            locale={{ emptyText: this.t('edf_task_step_empty', '暂无步骤记录') }}
                        />
                    </>
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px 0', color: 'rgba(0,0,0,0.45)' }}>
                        {this.t('edf_task_detail_fetch_fail', '获取任务详情失败')}
                    </div>
                )}
            </Modal>
        )
    }

    render() {
        if (Global.isMobile) return this.renderMobile()
        return this.renderPc()
    }

    renderMobile() {
        const { tasks } = this.props
        const { taskSize, taskPage } = this.state
        const { list = [], totalPages = 0 } = tasks

        const curP = tasks.page ?? taskPage
        const totalR = tasks.totalRecords ?? totalPages * taskSize

        return (
            <Content
                style={{
                    margin: '4px',
                    padding: '6px',
                    background: 'var(--content-bg)',
                    minHeight: '280px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Card
                    title={
                        <Space>
                            <FileTextOutlined style={{ color: '#1890ff' }} />
                            <Title level={4} style={{ margin: 0 }}>
                                {this.t('edf_task_title', 'EDF 任务')}
                            </Title>
                            <Tag color="blue">
                                {this.t('edf_task_total_records', '{count} 条记录')
                                    .replace('{count}', String(totalR))}
                            </Tag>
                        </Space>
                    }
                    extra={
                        <Button icon={<ReloadOutlined />} onClick={this.refresh}>
                            {this.t('common_refresh', '刷新')}
                        </Button>
                    }
                    style={{
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        background: 'var(--card-bg)',
                    }}
                    bodyStyle={{
                        padding: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                    }}
                >
                    <div style={{ flex: 1, minHeight: 0 }}>
                        <Table<EdfTaskItem>
                            rowKey="taskId"
                            columns={this.getMobileColumns()}
                            dataSource={list}
                            pagination={false}
                            size="small"
                            bordered
                        />
                    </div>

                    <Row
                        justify="space-between"
                        align="middle"
                        style={{
                            marginTop: 16,
                            flexShrink: 0,
                            paddingTop: 8,
                            borderTop: '1px solid #f0f0f0',
                        }}
                    >
                        <Col>
                            <Text type="secondary">
                                {this.t('edf_task_page_info', '共 {total} 条记录，当前第 {cur}/{pages} 页')
                                    .replace('{total}', String(totalR))
                                    .replace('{cur}', String(curP))
                                    .replace('{pages}', String(totalPages))}
                            </Text>
                        </Col>
                        <Col>
                            <Space>
                                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
                                    {this.t('common_per_page', '每页')}
                                </span>
                                <Select
                                    value={taskSize}
                                    onChange={this.onLppChange}
                                    style={{ width: 80 }}
                                    options={DEFAULT_RPP.map((item: number) => ({
                                        label: item,
                                        value: item,
                                    }))}
                                />
                                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
                                    {this.t('common_items', '条')}
                                </span>
                                <Pagination
                                    current={curP}
                                    total={totalR}
                                    pageSize={taskSize}
                                    onChange={this.onTaskPageChange}
                                    showSizeChanger={false}
                                    simple
                                    size="small"
                                />
                            </Space>
                        </Col>
                    </Row>
                </Card>

                {this.renderDetailModal()}
            </Content>
        )
    }

    renderPc() {
        const { tasks } = this.props
        const { taskSize, taskPage } = this.state
        const { list = [], totalPages = 0 } = tasks

        const curP = tasks.page ?? taskPage
        const totalR = tasks.totalRecords ?? totalPages * taskSize

        // 计算表格滚动高度
        const tableScrollY = window.innerHeight - 330
        const rowHeight = 40
        const headerHeight = 38
        const availableHeight = tableScrollY - headerHeight
        const totalContentHeight = list.length * rowHeight
        const needScroll = totalContentHeight > availableHeight

        return (
            <Content
                style={{
                    margin: '4px',
                    padding: '6px',
                    background: 'var(--content-bg)',
                    minHeight: '280px',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <Card
                    title={
                        <Space>
                            <FileTextOutlined style={{ color: '#1890ff' }} />
                            <Title level={4} style={{ margin: 0 }}>
                                {this.t('edf_task_title', 'EDF 任务')}
                            </Title>
                            <Tag color="blue">
                                {this.t('edf_task_total_records', '{count} 条记录')
                                    .replace('{count}', String(totalR))}
                            </Tag>
                        </Space>
                    }
                    extra={
                        <Button icon={<ReloadOutlined />} onClick={this.refresh}>
                            {this.t('common_refresh', '刷新')}
                        </Button>
                    }
                    style={{
                        borderRadius: 8,
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        background: 'var(--card-bg)',
                    }}
                    bodyStyle={{
                        padding: '6px',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                    }}
                >
                    {/* 表格区域 - 可滚动 */}
                    <div style={{ flex: 1, minHeight: 0 }}>
                        <Table<EdfTaskItem>
                            rowKey="taskId"
                            columns={this.getPcColumns()}
                            dataSource={list}
                            pagination={false}
                            bordered
                            size="small"
                            style={{ borderRadius: 6, height: '100%' }}
                            scroll={{
                                y: needScroll
                                    ? tableScrollY > 200
                                        ? tableScrollY
                                        : 300
                                    : undefined,
                            }}
                        />
                    </div>

                    {/* 分页区域 - 固定在底部 */}
                    <Row
                        justify="space-between"
                        align="middle"
                        style={{
                            marginTop: 16,
                            flexShrink: 0,
                            paddingTop: 8,
                            borderTop: '1px solid #f0f0f0',
                        }}
                    >
                        <Col>
                            <Text type="secondary">
                                {this.t('edf_task_page_info', '共 {total} 条记录，当前第 {cur}/{pages} 页')
                                    .replace('{total}', String(totalR))
                                    .replace('{cur}', String(curP))
                                    .replace('{pages}', String(totalPages))}
                            </Text>
                        </Col>
                        <Col>
                            <Space>
                                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
                                    {this.t('common_per_page', '每页')}
                                </span>
                                <Select
                                    value={taskSize}
                                    onChange={this.onLppChange}
                                    style={{ width: 80 }}
                                    options={DEFAULT_RPP.map((item: number) => ({
                                        label: item,
                                        value: item,
                                    }))}
                                />
                                <span style={{ color: 'rgba(0,0,0,0.45)', fontSize: 14 }}>
                                    {this.t('common_items', '条')}
                                </span>
                                <Pagination
                                    current={curP}
                                    total={totalR}
                                    pageSize={taskSize}
                                    onChange={this.onTaskPageChange}
                                    showSizeChanger={false}
                                    showQuickJumper
                                />
                            </Space>
                        </Col>
                    </Row>
                </Card>

                {this.renderDetailModal()}
            </Content>
        )
    }
}